import "@fontsource-variable/montserrat";

import {
  AppBar,
  CircularProgressCenter,
  Drawer,
  FireCMS,
  ModeControllerProvider,
  NavigationRoutes,
  Scaffold,
  SideDialogs,
  SnackbarProvider,
  useBuildLocalConfigurationPersistence,
  useBuildModeController,
  useBuildNavigationController,
  useValidateAuthenticator,
} from "@firecms/core";
import {
  FirebaseAuthController,
  FirebaseLoginView,
  FirebaseSignInProvider,
  useFirebaseAuthController,
  useFirebaseStorageSource,
  useFirestoreDelegate,
  useInitialiseFirebase,
} from "@firecms/firebase";
import { CenteredView } from "@firecms/ui";
import { useMemo } from "react";

import darkLogo from "./assets/deardoc.png";
import logo from "./assets/logow.png";
import {
  appointmentConnectionsCollections,
  fhirConnectionsCollections,
  modmedCustomersCollection,
  nexHealthCustomersCollection,
  teamMembersCollection,
} from "./collections";
import { appleDelegatedOnboardingCollection } from "./collections/apple/apple_delegated_onboarding";
import { appleGrantStatusCollection } from "./collections/apple/apple_grant_status";
import { googleActionEntitiesCollection } from "./collections/google_actions";
import { firebaseProductionConfig } from "./firebase_config";
import { authenticator } from "./services/auth";
import { algoliaSearchControllerBuilder } from "./text_search";

const firebaseConfig = firebaseProductionConfig;

export default function App() {
  const collections = useMemo(
    () => [
      appointmentConnectionsCollections,
      fhirConnectionsCollections,
      // `apple_actions_entity` and `apple_brands_entity` were unregistered here: they surface the
      // frozen legacy Apple data, which nobody works from operationally. The DOCUMENTS are still
      // live — the deployed `oAuth` function keeps writing to them — so only the screens are gone.
      appleGrantStatusCollection,
      // Sits next to the grant-status projection under "Partners": the two are halves of one staff
      // flow — you link a client there, then put their practice on Apple Maps here. The showcase
      // subcollection is declared only as a subcollection, so it renders as a tab inside a record
      // rather than as its own nav item.
      appleDelegatedOnboardingCollection,
      nexHealthCustomersCollection,
      modmedCustomersCollection,
      googleActionEntitiesCollection,
      teamMembersCollection,
    ],
    [],
  );

  const { firebaseApp, firebaseConfigLoading, configError } = useInitialiseFirebase({
    firebaseConfig,
  });

  // Controller used to manage the dark or light color mode
  const modeController = useBuildModeController();

  const signInOptions: FirebaseSignInProvider[] = ["google.com", "password"];

  // Controller for managing authentication
  const authController: FirebaseAuthController = useFirebaseAuthController({
    firebaseApp,
    signInOptions,
  });

  // Controller for saving some user preferences locally.
  const userConfigPersistence = useBuildLocalConfigurationPersistence();

  // Delegate used for fetching and saving data in Firestore
  const firestoreDelegate = useFirestoreDelegate({
    firebaseApp,
    localTextSearchEnabled: true,
    textSearchControllerBuilder: algoliaSearchControllerBuilder,
  });

  const storageSource = useFirebaseStorageSource({
    firebaseApp,
  });

  const { authLoading, canAccessMainView, notAllowedError } = useValidateAuthenticator({
    authController,
    authenticator,
    dataSourceDelegate: firestoreDelegate,
    storageSource,
  });

  const navigationController = useBuildNavigationController({
    collections,
    authController,
    dataSourceDelegate: firestoreDelegate,
  });

  if (firebaseConfigLoading || !firebaseApp) {
    return (
      <>
        <CircularProgressCenter />
      </>
    );
  }

  if (configError) {
    return <CenteredView>{configError}</CenteredView>;
  }
  return (
    <SnackbarProvider>
      <ModeControllerProvider value={modeController}>
        <FireCMS
          navigationController={navigationController}
          authController={authController}
          userConfigPersistence={userConfigPersistence}
          dataSourceDelegate={firestoreDelegate}
          storageSource={storageSource}
        >
          {({ loading }) => {
            if (loading || authLoading) {
              return <CircularProgressCenter size={"large"} />;
            }

            if (!canAccessMainView) {
              return (
                <FirebaseLoginView
                  authController={authController}
                  firebaseApp={firebaseApp}
                  logo={modeController.mode === "light" ? darkLogo : logo}
                  signInOptions={signInOptions}
                  notAllowedError={notAllowedError}
                />
              );
            }

            return (
              <Scaffold
                autoOpenDrawer={false}
                logo={modeController.mode === "light" ? darkLogo : logo}
              >
                <AppBar title={"Integrations Admin"} />
                <Drawer />
                <NavigationRoutes />
                <SideDialogs />
              </Scaffold>
            );
          }}
        </FireCMS>
      </ModeControllerProvider>
    </SnackbarProvider>
  );
}
